import { PaymentForm } from "@/components/stripe/PaymentForm";
import { StripeProvider } from "@/components/stripe/StripeProvider";
import type { SubscriptionPlan } from "@/models/subscription/subscription.model";
import { createSubscription } from "@/services/subscription-service";
import {
	CheckCircleOutlined,
	CreditCardOutlined,
	CrownOutlined,
} from "@ant-design/icons";
import {
	Button,
	Card,
	Divider,
	Modal,
	Space,
	Steps,
	Typography,
	message,
} from "antd";
import React, { useState } from "react";

const { Title, Text } = Typography;

interface SubscriptionModalProps {
	open: boolean;
	onClose: () => void;
	plan: SubscriptionPlan | null;
	onSuccess: () => void;
}

export const SubscriptionModal: React.FC<SubscriptionModalProps> = ({
	open,
	onClose,
	plan,
	onSuccess,
}) => {
	const [currentStep, setCurrentStep] = useState(0);
	const [paymentMethodId, setPaymentMethodId] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);

	const handlePaymentMethodCreated = (pmId: string) => {
		setPaymentMethodId(pmId);
		setCurrentStep(1);
		message.success("Payment method added successfully!");
	};

	const handlePaymentError = (error: string) => {
		message.error(`Payment error: ${error}`);
	};

	const handleCreateSubscription = async () => {
		if (!plan || !paymentMethodId) return;

		try {
			setLoading(true);
			const result = await createSubscription(
				plan.id.toString(),
				paymentMethodId,
			);

			if (result.clientSecret) {
				// Handle 3D Secure authentication if needed
				message.info("Payment authentication may be required");
				// You could implement 3D Secure flow here
			}

			setCurrentStep(2);
			message.success(`Successfully subscribed to ${plan.name}!`);

			// Close modal and refresh data after a short delay
			setTimeout(() => {
				onSuccess();
				handleClose();
			}, 2000);
		} catch (error) {
			console.error("Failed to create subscription:", error);
			message.error("Failed to create subscription. Please try again.");
		} finally {
			setLoading(false);
		}
	};

	const handleClose = () => {
		setCurrentStep(0);
		setPaymentMethodId(null);
		setLoading(false);
		onClose();
	};

	if (!plan) return null;

	const steps = [
		{
			title: "Payment Method",
			icon: <CreditCardOutlined />,
		},
		{
			title: "Confirm Subscription",
			icon: <CrownOutlined />,
		},
		{
			title: "Complete",
			icon: <CheckCircleOutlined />,
		},
	];

	return (
		<Modal
			title={`Subscribe to ${plan.name}`}
			open={open}
			onCancel={handleClose}
			footer={null}
			width={600}
			destroyOnClose
		>
			<Steps
				current={currentStep}
				items={steps}
				style={{ marginBottom: "32px" }}
			/>

			{currentStep === 0 && (
				<div>
					<Title level={4}>Add Payment Method</Title>
					<Text
						type="secondary"
						style={{ display: "block", marginBottom: "24px" }}
					>
						Please enter your payment information to subscribe to {plan.name}.
					</Text>

					<StripeProvider>
						<PaymentForm
							onPaymentMethodCreated={handlePaymentMethodCreated}
							onError={handlePaymentError}
							buttonText="Continue to Subscription"
						/>
					</StripeProvider>
				</div>
			)}

			{currentStep === 1 && (
				<div>
					<Title level={4}>Confirm Your Subscription</Title>

					<Card style={{ marginBottom: "24px" }}>
						<div style={{ textAlign: "center" }}>
							<Title level={3} style={{ margin: 0, color: "#1890ff" }}>
								{plan.name}
							</Title>
							<div style={{ margin: "16px 0" }}>
								<Text
									style={{
										fontSize: "36px",
										fontWeight: "bold",
										color: "#1890ff",
									}}
								>
									${(plan.price / 100).toFixed(0)}
								</Text>
								<Text style={{ fontSize: "16px", color: "#666" }}>
									/{plan.interval}
								</Text>
							</div>
							<Text type="secondary">{plan.description}</Text>
						</div>

						<Divider />

						<div>
							<Text strong style={{ display: "block", marginBottom: "12px" }}>
								What's included:
							</Text>
							<ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
								{plan.features.map((feature, index) => (
									<li
										key={index}
										style={{
											marginBottom: "8px",
											display: "flex",
											alignItems: "flex-start",
										}}
									>
										<span
											style={{
												color: "#52c41a",
												marginRight: "8px",
												fontSize: "16px",
											}}
										>
											✓
										</span>
										<Text>{feature}</Text>
									</li>
								))}
							</ul>
						</div>
					</Card>

					<Space style={{ width: "100%", justifyContent: "space-between" }}>
						<Button onClick={() => setCurrentStep(0)}>Back to Payment</Button>
						<Button
							type="primary"
							size="large"
							loading={loading}
							onClick={handleCreateSubscription}
							icon={<CrownOutlined />}
						>
							Subscribe Now
						</Button>
					</Space>
				</div>
			)}

			{currentStep === 2 && (
				<div style={{ textAlign: "center", padding: "32px 0" }}>
					<CheckCircleOutlined
						style={{ fontSize: "64px", color: "#52c41a", marginBottom: "16px" }}
					/>
					<Title level={3}>Subscription Created!</Title>
					<Text
						type="secondary"
						style={{ display: "block", marginBottom: "24px" }}
					>
						Welcome to {plan.name}! Your subscription is now active.
					</Text>
					<Text type="secondary">
						Redirecting to your subscription dashboard...
					</Text>
				</div>
			)}
		</Modal>
	);
};
